/** Fixed-width token substituted for mention and inline emoji entities in text estimates. */
export const HTML_ENTITY_TEXT_TOKEN = '[]';

const ENTITY_ELEMENT_PATTERN =
  /<(span|a)\b(?=[^>]*\bdata-(?:mention|emoji|custom-emoji|inline-emoji)(?:\b|=|-))[^>]*>[\s\S]*?<\/\1>/gi;
const ENTITY_VOID_PATTERN =
  /<(?:img)\b(?=[^>]*\bdata-(?:emoji|custom-emoji|inline-emoji)(?:\b|=|-))[^>]*\/?>/gi;
const BLOCK_BREAK_PATTERN = /<\/(?:p|div|li|h[1-6])\s*>|<br\s*\/?>/gi;
const TAG_PATTERN = /<[^>]*>/g;

const BASIC_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' '
};

/**
 * Converts sanitized backend HTML into the plain estimator string.
 * Mention chips and inline custom emoji entities use a deterministic two-character token.
 */
export function textFromSanitizedHtml(html: string): string {
  return decodeBasicEntities(
    html
      .replace(ENTITY_ELEMENT_PATTERN, HTML_ENTITY_TEXT_TOKEN)
      .replace(ENTITY_VOID_PATTERN, HTML_ENTITY_TEXT_TOKEN)
      .replace(BLOCK_BREAK_PATTERN, ' ')
      .replace(TAG_PATTERN, '')
  )
    .replace(/\s+/g, ' ')
    .trim();
}

/** Escapes text for HTML text-node insertion; does not mark the result as trusted HTML. */
export function escapeHtmlText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function decodeBasicEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, decodeEntity);
}

function decodeEntity(match: string, entity: string): string {
  const lower = entity.toLowerCase();
  if (Object.hasOwn(BASIC_ENTITIES, lower)) return BASIC_ENTITIES[lower];

  const codePoint = lower.startsWith('#x')
    ? Number.parseInt(lower.slice(2), 16)
    : lower.startsWith('#')
      ? Number.parseInt(lower.slice(1), 10)
      : NaN;
  if (!Number.isInteger(codePoint) || codePoint < 0 || codePoint > 0x10ffff) return match;

  try {
    return String.fromCodePoint(codePoint);
  } catch {
    return match;
  }
}
